import React, { useRef, useEffect, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import Globe from 'react-globe.gl';
import { useLanguage } from '../LanguageContext';
import { useTheme } from '../ThemeContext';
import { useData } from '../DataContext';

const Hero = () => {
  const globeRef = useRef();
  const [globeReady, setGlobeReady] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 800, height: 800 });
  const { t, language } = useLanguage();
  const { isDark } = useTheme();
  const { hero, loading } = useData();

  useEffect(() => {
    // Set globe size based on window
    const updateDimensions = () => {
      const size = Math.min(window.innerWidth * 0.55, window.innerHeight * 0.9, 900);
      setDimensions({ width: size, height: size });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    if (globeRef.current && globeReady) {
      // Auto-rotate
      globeRef.current.controls().autoRotate = true;
      globeRef.current.controls().autoRotateSpeed = 0.3;
      globeRef.current.controls().enableZoom = false;

      // Set initial position - show Turkey/Europe
      globeRef.current.pointOfView({ lat: 35, lng: 30, altitude: 2.2 });
    }
  }, [globeReady]);

  // Arc data - ALL ARCS START FROM ISTANBUL to different cities worldwide
  const arcsData = [
    { startLat: 41.0082, startLng: 28.9784, endLat: 40.7128, endLng: -74.006, color: ['#22d3ee', '#8b5cf6'] },   // Istanbul -> New York
    { startLat: 41.0082, startLng: 28.9784, endLat: 51.5074, endLng: -0.1278, color: ['#22d3ee', '#00d4ff'] },   // Istanbul -> London
    { startLat: 41.0082, startLng: 28.9784, endLat: 35.6762, endLng: 139.6503, color: ['#22d3ee', '#8b5cf6'] },  // Istanbul -> Tokyo
    { startLat: 41.0082, startLng: 28.9784, endLat: 48.8566, endLng: 2.3522, color: ['#22d3ee', '#00d4ff'] },    // Istanbul -> Paris
    { startLat: 41.0082, startLng: 28.9784, endLat: -33.8688, endLng: 151.2093, color: ['#22d3ee', '#8b5cf6'] }, // Istanbul -> Sydney
    { startLat: 41.0082, startLng: 28.9784, endLat: 55.7558, endLng: 37.6173, color: ['#22d3ee', '#00d4ff'] },   // Istanbul -> Moscow
    { startLat: 41.0082, startLng: 28.9784, endLat: 25.2048, endLng: 55.2708, color: ['#22d3ee', '#00d4ff'] },   // Istanbul -> Dubai
    { startLat: 41.0082, startLng: 28.9784, endLat: 1.3521, endLng: 103.8198, color: ['#22d3ee', '#8b5cf6'] },   // Istanbul -> Singapore
    { startLat: 41.0082, startLng: 28.9784, endLat: 52.52, endLng: 13.405, color: ['#22d3ee', '#00d4ff'] },      // Istanbul -> Berlin
    { startLat: 41.0082, startLng: 28.9784, endLat: 34.0522, endLng: -118.2437, color: ['#22d3ee', '#8b5cf6'] }, // Istanbul -> Los Angeles
    { startLat: 41.0082, startLng: 28.9784, endLat: -23.5505, endLng: -46.6333, color: ['#22d3ee', '#8b5cf6'] }, // Istanbul -> São Paulo
    { startLat: 41.0082, startLng: 28.9784, endLat: 28.6139, endLng: 77.209, color: ['#22d3ee', '#00d4ff'] },    // Istanbul -> Delhi
  ];

  // Labels data - countries and cities with names (ENGLISH)
  const labelsData = [
    // Major Cities
    { lat: 40.7128, lng: -74.006, text: 'New York', color: '#00d4ff', size: 0.8 },
    { lat: 51.5074, lng: -0.1278, text: 'London', color: '#00d4ff', size: 0.8 },
    { lat: 35.6762, lng: 139.6503, text: 'Tokyo', color: '#00d4ff', size: 0.8 },
    { lat: 41.0082, lng: 28.9784, text: 'Istanbul', color: '#22d3ee', size: 1.2 },
    { lat: 48.8566, lng: 2.3522, text: 'Paris', color: '#00d4ff', size: 0.8 },
    { lat: -33.8688, lng: 151.2093, text: 'Sydney', color: '#00d4ff', size: 0.8 },
    { lat: 55.7558, lng: 37.6173, text: 'Moscow', color: '#00d4ff', size: 0.8 },
    { lat: 39.9042, lng: 116.4074, text: 'Beijing', color: '#00d4ff', size: 0.8 },
    { lat: 37.5665, lng: 126.978, text: 'Seoul', color: '#00d4ff', size: 0.8 },
    { lat: 1.3521, lng: 103.8198, text: 'Singapore', color: '#00d4ff', size: 0.8 },
    { lat: 25.2048, lng: 55.2708, text: 'Dubai', color: '#00d4ff', size: 0.7 },
    { lat: 52.52, lng: 13.405, text: 'Berlin', color: '#00d4ff', size: 0.7 },
    { lat: 34.0522, lng: -118.2437, text: 'Los Angeles', color: '#00d4ff', size: 0.7 },
    { lat: 19.4326, lng: -99.1332, text: 'Mexico City', color: '#00d4ff', size: 0.7 },
    { lat: -23.5505, lng: -46.6333, text: 'São Paulo', color: '#00d4ff', size: 0.7 },
    { lat: 28.6139, lng: 77.209, text: 'Delhi', color: '#00d4ff', size: 0.7 },
    { lat: 31.2304, lng: 121.4737, text: 'Shanghai', color: '#00d4ff', size: 0.7 },
    { lat: -34.6037, lng: -58.3816, text: 'Buenos Aires', color: '#00d4ff', size: 0.7 },
    { lat: 30.0444, lng: 31.2357, text: 'Cairo', color: '#00d4ff', size: 0.7 },
    { lat: 39.9334, lng: 32.8597, text: 'Ankara', color: '#22d3ee', size: 0.9 },
    // Countries
    { lat: 39.0, lng: 35.0, text: 'TURKEY', color: '#22d3ee', size: 1.5 },
    { lat: 46.2276, lng: 2.2137, text: 'FRANCE', color: '#a5b4fc', size: 1.0 },
    { lat: 51.1657, lng: 10.4515, text: 'GERMANY', color: '#a5b4fc', size: 1.0 },
    { lat: 55.3781, lng: -3.436, text: 'UK', color: '#a5b4fc', size: 1.0 },
    { lat: 41.8719, lng: 12.5674, text: 'ITALY', color: '#a5b4fc', size: 0.9 },
    { lat: 40.4637, lng: -3.7492, text: 'SPAIN', color: '#a5b4fc', size: 0.9 },
    { lat: 61.524, lng: 105.3188, text: 'RUSSIA', color: '#a5b4fc', size: 1.2 },
    { lat: 35.8617, lng: 104.1954, text: 'CHINA', color: '#a5b4fc', size: 1.3 },
    { lat: 36.2048, lng: 138.2529, text: 'JAPAN', color: '#a5b4fc', size: 1.0 },
    { lat: 20.5937, lng: 78.9629, text: 'INDIA', color: '#a5b4fc', size: 1.1 },
    { lat: 37.0902, lng: -95.7129, text: 'USA', color: '#a5b4fc', size: 1.3 },
    { lat: 56.1304, lng: -106.3468, text: 'CANADA', color: '#a5b4fc', size: 1.1 },
    { lat: -14.235, lng: -51.9253, text: 'BRAZIL', color: '#a5b4fc', size: 1.1 },
    { lat: -25.2744, lng: 133.7751, text: 'AUSTRALIA', color: '#a5b4fc', size: 1.1 },
    { lat: 26.8206, lng: 30.8025, text: 'EGYPT', color: '#a5b4fc', size: 0.9 },
    { lat: 23.4241, lng: 53.8478, text: 'UAE', color: '#a5b4fc', size: 0.8 },
    { lat: 23.6345, lng: -102.5528, text: 'MEXICO', color: '#a5b4fc', size: 1.0 },
  ];

  // Points data - major cities (dots on globe)
  const pointsData = [
    { lat: 40.7128, lng: -74.006, size: 0.15, color: '#00d4ff' },
    { lat: 51.5074, lng: -0.1278, size: 0.15, color: '#00d4ff' },
    { lat: 35.6762, lng: 139.6503, size: 0.15, color: '#00d4ff' },
    { lat: 41.0082, lng: 28.9784, size: 0.25, color: '#22d3ee' },
    { lat: 48.8566, lng: 2.3522, size: 0.15, color: '#00d4ff' },
    { lat: -33.8688, lng: 151.2093, size: 0.15, color: '#00d4ff' },
    { lat: 55.7558, lng: 37.6173, size: 0.15, color: '#00d4ff' },
    { lat: 39.9042, lng: 116.4074, size: 0.15, color: '#00d4ff' },
    { lat: 37.5665, lng: 126.978, size: 0.15, color: '#00d4ff' },
    { lat: 1.3521, lng: 103.8198, size: 0.15, color: '#00d4ff' },
    { lat: 39.9334, lng: 32.8597, size: 0.2, color: '#22d3ee' },
    { lat: 25.2048, lng: 55.2708, size: 0.12, color: '#00d4ff' },
    { lat: 52.52, lng: 13.405, size: 0.12, color: '#00d4ff' },
  ];

  // Show loading state while data is being fetched
  if (loading || !hero.data) {
    return (
      <section className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </section>
    );
  }

  return (
    <section id="home" className={`min-h-screen relative overflow-hidden ${isDark ? 'bg-[#0a0f1a]' : 'bg-[#f8fafc]'}`}>
      {/* Animated Background */}
      <div className="absolute inset-0">
        <div className={`absolute inset-0 ${isDark ? 'bg-[#0a0f1a]' : 'bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100'}`}></div>
        {/* Animated grid pattern - daha hafif aydınlık modda */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: isDark
              ? `linear-gradient(rgba(14, 165, 233, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(14, 165, 233, 0.1) 1px, transparent 1px)`
              : `linear-gradient(rgba(148, 163, 184, 0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(148, 163, 184, 0.15) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        ></div>
        {/* Floating orbs - daha soft aydınlık modda */}
        <div className={`absolute top-1/4 left-1/4 w-96 h-96 ${isDark ? 'bg-blue-500/10' : 'bg-blue-300/15'} rounded-full blur-[100px] animate-pulse`}></div>
        <div className={`absolute bottom-1/4 right-1/4 w-80 h-80 ${isDark ? 'bg-blue-500/10' : 'bg-indigo-300/15'} rounded-full blur-[80px] animate-pulse`} style={{ animationDelay: '1s' }}></div>
      </div>

      {/* Main Content - Centered Layout */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 lg:px-12 py-8">
        <div className="w-full max-w-7xl mx-auto flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16">

          {/* Left Section - Hero Text */}
          <div className="space-y-6 lg:space-y-8 lg:max-w-xl flex-shrink-0 text-center lg:text-left">
            {/* Name & Main Headline */}
            <div>
              {/* Name - Elegant & Smaller */}
              <p className={`text-xl md:text-2xl font-light tracking-widest uppercase mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                Muhammed Salih DEDE
              </p>

              {/* Title - Bold & Large */}
              <h1 className={`text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black leading-none mb-6 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {hero.getLocalized('title', language) || t.hero.title1}
                <br />
                <span className="text-blue-500">
                  AI & ML
                </span>
              </h1>

              {/* Motto - Italic Style */}
              <p className={`text-lg md:text-xl lg:text-2xl italic font-light mb-4 border-l-4 border-blue-500 pl-4 ${isDark ? 'text-blue-300/80' : 'text-blue-600/80'}`}>
                {hero.getLocalized('subtitle', language) || t.hero.motto}
              </p>

              {/* Sub-headline */}
              <p className={`text-base md:text-lg leading-relaxed font-light ${isDark ? 'text-gray-500' : 'text-gray-600'}`}>
                {hero.getLocalized('description', language) || t.hero.description}
              </p>
            </div>

            {/* Value Proposition */}
            <div className={`py-4 px-6 rounded-2xl backdrop-blur-sm ${isDark ? 'bg-blue-500/10 border border-blue-500/20' : 'bg-white/70 border border-slate-200 shadow-sm'}`}>
              <p className={`text-base md:text-lg leading-relaxed font-light ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
                {t.hero.valueProposition}
                <span className={`font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}> {t.hero.nlp}</span>,
                <span className={`font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}> {t.hero.deepLearning}</span>, and
                <span className={`font-semibold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}> {t.hero.automation}</span>{t.hero.valueProposition2}
              </p>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
              <a
                href="#projects"
                className="group relative inline-flex items-center justify-center px-8 py-4 bg-blue-600 rounded-xl text-white font-semibold overflow-hidden transition-all duration-300 hover:shadow-lg hover:shadow-blue-500/30 hover:scale-105"
              >
                <span className="relative z-10 flex items-center">
                  {t.hero.exploreSolutions}
                  <ExternalLink className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </span>
                <div className="absolute inset-0 bg-blue-700 opacity-0 group-hover:opacity-100 transition-opacity"></div>
              </a>

              <a
                href="#contact"
                className={`group inline-flex items-center justify-center px-8 py-4 border-2 rounded-xl font-semibold transition-all duration-300 hover:scale-105 ${
                  isDark
                    ? 'border-blue-500/50 text-blue-400 hover:bg-blue-500/10 hover:border-blue-400'
                    : 'border-blue-500 text-blue-600 hover:bg-blue-50 hover:border-blue-600'
                }`}
              >
                {t.hero.startProject}
                <ExternalLink className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </div>

          {/* Right Section - 3D Globe - MUCH BIGGER */}
          <div className="relative flex items-center justify-center flex-1">
            {/* Glow effect behind globe */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className={`w-[600px] h-[600px] ${isDark ? 'bg-blue-500/15' : 'bg-blue-400/20'} rounded-full blur-[120px]`}></div>
            </div>

            {/* Globe Container - Responsive & Large */}
            <div className="relative" style={{ width: dimensions.width, height: dimensions.height }}>
              <Globe
                ref={globeRef}
                onGlobeReady={() => setGlobeReady(true)}

                // Globe appearance
                globeImageUrl="//unpkg.com/three-globe/example/img/earth-blue-marble.jpg"
                bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
                backgroundImageUrl=""

                // Atmosphere
                atmosphereColor="#0ea5e9"
                atmosphereAltitude={0.15}

                // Labels - Country and City Names
                labelsData={labelsData}
                labelLat={d => d.lat}
                labelLng={d => d.lng}
                labelText={d => d.text}
                labelSize={d => d.size}
                labelColor={d => d.color}
                labelDotRadius={0.4}
                labelAltitude={0.01}
                labelResolution={2}

                // Arcs (connection lines)
                arcsData={arcsData}
                arcColor="color"
                arcDashLength={0.5}
                arcDashGap={0.2}
                arcDashAnimateTime={2000}
                arcStroke={0.5}

                // Points (cities dots)
                pointsData={pointsData}
                pointAltitude={0.01}
                pointColor="color"
                pointRadius="size"

                // Size - Dynamic
                width={dimensions.width}
                height={dimensions.height}

                // Background
                backgroundColor="rgba(0,0,0,0)"
              />
            </div>
          </div>
        </div>
      </div>

    </section>
  );
};

export default Hero;
